import { CancelablePromise, OpenAPI, request as __request } from '/src/api';
import { Statement, TPaymentId, TStatementId } from '/src/lib/models';
import type {
  PayoffEstimate,
  TCreatePaymentRequest,
  TCreatePaymentResponse,
  TDeletePaymentResponse,
  TGetAssetResponse,
  TGetPaymentResponse,
  TGetPayoffEstimateReportResponse,
  TGetPayoffEstimateRequest,
  TGetPayoffEstimateResponse,
  TListPaymentsRequest,
  TListPaymentsResponse,
  TUpdatePaymentRequest,
  TUpdatePaymentResponse,
} from '/src/lib/types';

// import { formatPayment, formatPaymentList } from '/src/utils';

export class PaymentService {
  // TODO: re-implement the request/backoff timer from `payments.client.service.js`

  /**
   * List Payments
   * @returns Payment[]
   * @throws ApiError
   */
  public static async query(req?: TListPaymentsRequest): CancelablePromise<TListPaymentsResponse> {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/payments',
      query: req,
    })) as TListPaymentsResponse;

    return res;
  }

  /**
   * Get Payment
   * @param id TPaymentId
   * @returns Payment
   * @throws ApiError
   */
  public static async get(id: TPaymentId): CancelablePromise<TGetPaymentResponse> {
    const paymentResponse = (await __request(OpenAPI, {
      method: 'GET',
      url: '/payments/{id}',
      path: { id },
    })) as TGetPaymentResponse;

    const assetResponse = (await __request(OpenAPI, {
      method: 'GET',
      url: '/assets/{id}',
      path: { id: paymentResponse.assetId },
    })) as TGetAssetResponse;

    paymentResponse.asset = assetResponse;

    return paymentResponse;
  }

  /**
   * Create Payment
   * @param payment Payment
   * @returns Payment
   * @throws ApiError
   */
  public static async create(req: TCreatePaymentRequest): CancelablePromise<TCreatePaymentResponse> {
    const res = (await __request(OpenAPI, {
      method: 'POST',
      url: '/payments',
      body: req,
    })) as TCreatePaymentResponse;

    return res;
  }

  /**
   * Update Payment
   * @param payment Payment
   * @returns Payment
   * @throws ApiError
   */
  public static async update(id: TPaymentId, req: TUpdatePaymentRequest): CancelablePromise<TUpdatePaymentResponse> {
    const res = (await __request(OpenAPI, {
      method: 'PUT',
      url: '/payments/{id}',
      path: { id },
      body: req,
    })) as TUpdatePaymentResponse;

    return res;
  }

  /**
   * Delete Payment
   * @param id TPaymentId
   * @returns Payment
   * @throws ApiError
   */
  public static async remove(id: TPaymentId): CancelablePromise<TDeletePaymentResponse> {
    const res = (await __request(OpenAPI, {
      method: 'DELETE',
      url: '/payments/{id}',
      path: { id },
    })) as TDeletePaymentResponse;

    return res;
  }

  /**
   * Get Payoff Estimate Report
   * @param id TLoanId
   * @returns Report
   * @throws ApiError
   */
  public static async getPayoffReport(id: TStatementId): CancelablePromise<PayoffEstimate | undefined> {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/payments/payoff-estimate-report/{id}',
      path: { id },
    })) as TGetPayoffEstimateReportResponse;

    return res;
  }

  /**
   * Get Payoff Estimate Statement
   * @param loanId TLoanId
   * @param asOfDate Date
   * @returns Payment
   * @throws ApiError
   */
  public static async estimatePayoff({ loanId, asOfDate }: TGetPayoffEstimateRequest): CancelablePromise<Statement> {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/payments/payoff-estimate-statement/{loanId}/{asOfDate}',
      path: { loanId, asOfDate: asOfDate.toISOString() },
    })) as TGetPayoffEstimateResponse;

    return res;
  }
}
